import React from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';

import * as styles from 'src/components/molecules/TaxonomyNav/TaxonomyNav.styles';

export interface TaxonomyNavItem {
  active: boolean;
  crawlable: boolean;
  href: string;
  text: string;
  value: string;
}

export interface TaxonomyNavProps {
  items: TaxonomyNavItem[];
}

interface NavItemProps {
  href: string;
  onNavigate: (href: string) => void;
  testId: string;
  text: string;
}

/** `current` variant — the current (deepest) category, rendered as non-clickable text. */
const ActiveNavItem = ({ testId, text }: NavItemProps) => (
  <span aria-current="page" css={styles.activeItem} data-testid={testId}>
    {text}
  </span>
);

/**
 * `link` variant — Next.js `<Link>` that renders a real `<a href>` (so search
 * engines can follow it) while doing a soft, shallow client-side transition.
 * Shallow + scroll=false mirror the action variant: the URL change is handled
 * by the taxonomy content sync, not a full server render or scroll-to-top.
 */
const CrawlableNavLink = ({ href, testId, text }: NavItemProps) => (
  <Link
    css={styles.link}
    data-testid={testId}
    data-text={text}
    href={href}
    prefetch={false}
    scroll={false}
    shallow
  >
    {text}
  </Link>
);

/** `action` variant — `<button data-href>` that delegates navigation to the parent. */
const NonCrawlableNavButton = ({ href, onNavigate, testId, text }: NavItemProps) => (
  <button
    css={styles.link}
    data-href={href}
    data-testid={testId}
    data-text={text}
    onClick={() => onNavigate(href)}
    type="button"
  >
    {text}
  </button>
);

export const TaxonomyNav = ({ items }: TaxonomyNavProps) => {
  const router = useRouter();

  const onItemClick = (href: string) => {
    router.push(href, undefined, { shallow: true, scroll: false });
  };

  return (
    <ul css={styles.list}>
      {items.map(({ active, crawlable, href, text, value }) => {
        let NavItem = NonCrawlableNavButton;
        if (active) {
          NavItem = ActiveNavItem;
        } else if (crawlable) {
          NavItem = CrawlableNavLink;
        }

        return (
          <li css={styles.item} key={value}>
            <NavItem
              href={href}
              onNavigate={onItemClick}
              testId={`taxonomy-nav-${value}`}
              text={text}
            />
          </li>
        );
      })}
    </ul>
  );
};
